import {takeLatest} from '@redux-saga/core/effects';
import {Async, Empty, Loaded, toEither} from 'Core/models/Async';
import {Password, ValidPassword} from 'Core/models/Password';
import * as E from 'fp-ts/lib/Either';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {AgentNumberStatusId} from 'Shared/constants/AgentNumberStatus';
import {Scope} from 'Shared/constants/Scope';
import {createAction} from 'Shared/utilities/actionCreator';
import {apiCallGenerator} from 'Shared/utilities/sagaApiGenerators';
import {PasswordUpdateBySecurityQuestionRequestDto} from 'Users/api/api';
import userApi from 'Users/api/userApi';
import {AgentAgencyClaim} from 'Users/models/AgentAgencyClaim';
import {AgentClaims} from 'Users/models/AgentClaims';
import {ValidAgentNumber} from 'Users/models/AgentNumber';
import {AsyncUserClaims, getAgentClaims, toOption, UserClaims} from 'Users/models/Claims';
import {getUserScopesUserProfile} from 'Users/selectors/userProfileSelectors';

export enum UserActionTypes {
  VALIDATE_ALLSTATE_USERNAME = 'userProfile/VALIDATE_ALLSTATE_USERNAME',
  VALIDATE_EXTERNAL_USERNAME = 'userProfile/VALIDATE_EXTERNAL_USERNAME',
  SET_SELECTED_AGENCY = 'user/SET_SELECTED_TAX_ID',
  SET_USER_CLAIMS = 'user/SET_USER_CLAIMS',
  GET_USER_CLAIMS = 'user/GET_USER_CLAIMS',
  CHANGE_PASSWORD = 'user/CHANGE_PASSWORD',
  UPDATE_PASSWORD = 'user/UPDATE_PASSWORD',
  CHANGE_UPDATE_PASSWORD_ERROR = 'user/CHANGE_UPDATE_PASSWORD_ERROR',
  REFRESH_USER_TOKEN = 'user/REFRESH_USER_TOKEN',
}

export const isAgencyInStatus = (status: AgentNumberStatusId) => (agency: AgentAgencyClaim) =>
  agency.overallAgentNumberStatus.id === status;
export const validateAllstateUsername = (username: string) =>
  createAction(UserActionTypes.VALIDATE_ALLSTATE_USERNAME, {username});
export const validateExternalUsername = (username: string) =>
  createAction(UserActionTypes.VALIDATE_EXTERNAL_USERNAME, {username});
export const setSelectedAgency = (selectedAgency: O.Option<AgentAgencyClaim>, isNewYork: boolean) =>
  createAction(UserActionTypes.SET_SELECTED_AGENCY, {
    selectedAgency,
    isNewYork,
  });
export const setUserClaims = (userClaims: AsyncUserClaims) =>
  createAction(UserActionTypes.SET_USER_CLAIMS, {userClaims});
export const getUserClaims = (username: string, changeAsyncData: (userClaims: Async<UserClaims>) => void) =>
  createAction(UserActionTypes.GET_USER_CLAIMS, {username, changeAsyncData});
export const changePassword = (value: Async<Password>) => createAction(UserActionTypes.CHANGE_PASSWORD, {value});
export const updatePassword = (
  userId: string,
  updatePasswordRequest: PasswordUpdateBySecurityQuestionRequestDto,
  onReset?: () => void
) => createAction(UserActionTypes.UPDATE_PASSWORD, {userId, updatePasswordRequest, onReset});
export const changeUpdatePasswordError = (passwordUpdateError?: string) =>
  createAction(UserActionTypes.CHANGE_UPDATE_PASSWORD_ERROR, {passwordUpdateError});

export const refreshUserToken = () => {
  return createAction(UserActionTypes.REFRESH_USER_TOKEN);
};

export const UserProfileActions = {
  getUserClaims,
  setSelectedAgency,
  setUserClaims,
  changePassword,
};

type UserProfileAction =
  | ReturnType<typeof setSelectedAgency>
  | ReturnType<typeof setUserClaims>
  | ReturnType<typeof changePassword>
  | ReturnType<typeof changeUpdatePasswordError>;

export type UserProfileState = {
  userClaims: AsyncUserClaims;
  currentAgentNumbers: ValidAgentNumber[];
  agentClaims: O.Option<AgentClaims>;
  selectedAgency: O.Option<AgentAgencyClaim>;
  scopes: Scope[];
  // For the sake of the pre-receive hook
  pwValue: Async<Password>;
  passwordUpdateError?: string;
};
export const DEFAULT_STATE: UserProfileState = {
  selectedAgency: O.none,
  userClaims: new Empty(),
  agentClaims: O.none,
  currentAgentNumbers: [],
  scopes: [],
  pwValue: new Loaded(ValidPassword.create('', '')),
};

export default (state: UserProfileState = DEFAULT_STATE, action: UserProfileAction): UserProfileState => {
  switch (action.type) {
    case UserActionTypes.SET_SELECTED_AGENCY:
      const {selectedAgency, isNewYork} = action.payload;

      return {
        ...state,
        selectedAgency: pipe(
          selectedAgency,
          O.fold(
            () => O.none,
            agency => O.some({...agency, isNewYork})
          )
        ),
        currentAgentNumbers: pipe(
          action.payload.selectedAgency,
          O.fold(
            () => [],
            agency => {
              return agency.agentNumbers.filter(agentNum => agentNum.isNewYork === action.payload.isNewYork);
            }
          )
        ),
      };

    case UserActionTypes.SET_USER_CLAIMS:
      return {
        ...state,
        userClaims: action.payload.userClaims,
        agentClaims: pipe(
          toOption(action.payload.userClaims),
          O.chain(claims => getAgentClaims(claims.userTypes))
        ),
        scopes: pipe(
          toEither(state.userClaims),
          E.fold(
            () => [],
            userClaims => getUserScopesUserProfile(state, userClaims)
          )
        ),
      };
    case UserActionTypes.CHANGE_PASSWORD:
      return {
        ...state,
        pwValue: action.payload.value,
      };
    case UserActionTypes.CHANGE_UPDATE_PASSWORD_ERROR:
      return {
        ...state,
        passwordUpdateError: action.payload.passwordUpdateError,
      };
    default:
      return state;
  }
};

export const retrieveUserClaims = (action: ReturnType<typeof getUserClaims>) => {
  const {username} = action.payload;

  return userApi.getUserClaims(username);
};

export function* getAgentClaimsGenerator() {
  yield takeLatest(UserActionTypes.GET_USER_CLAIMS, apiCallGenerator(retrieveUserClaims));
}
